import type { FormEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Bot,
  MessageCircle,
  RotateCcw,
  SendHorizontal,
  Loader2,
  StopCircle,
  Sparkles,
  User,
  Wand2,
  Download,
  SlidersHorizontal,
  BookmarkPlus,
  Edit2,
  Check,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Info } from 'lucide-react';

import { useCollectionChat } from './use-collection-chat';
import { SavePromptForm } from './save-prompt-form';
import { ManagePromptsModal } from './manage-prompts-modal';
import { SourceDetailsSidebar } from './source-details-sidebar';
import { CollectionChatMessage } from './types';

const DEFAULT_EXAMPLES = [
  'How many Qdrant points are indexed here?',
  'Summarize the newest documents in this collection.',
  'What sources were ingested most recently?',
];

interface SavedPrompt {
  text: string;
  chat_mode: string;
}

interface CollectionChatCardProps {
  orgId?: string | null;
  collectionId?: string | null;
  collectionName?: string | null;
  authToken?: string | null;
  className?: string;
  examplePrompts?: string[];
  disabled?: boolean;
  onCustomizePrompt?: () => void;
  customPromptActive?: boolean;
  onOpenChatFilters?: () => void;
  isAdmin?: boolean;
  savedPrompts?: SavedPrompt[];
  onSavePrompt?: (
    prompt: string,
    chatMode: string,
    displayName?: string,
  ) => Promise<void>;
  onDeletePrompt?: (index: number) => Promise<void>;
  onReorderPrompts?: (prompts: SavedPrompt[]) => Promise<void>;
}

const ChatMessageBubble = ({
  message,
  onSave,
  savedPrompts,
  chatMode,
  onSwitchToChatMode,
  onSourceClick,
}: {
  message: CollectionChatMessage;
  onSave?: (content: string, chatMode: string) => void;
  savedPrompts?: SavedPrompt[];
  chatMode: string;
  onSwitchToChatMode?: () => void;
  onSourceClick?: (message: CollectionChatMessage, sourceIndex: number) => void;
}) => {
  const isAssistant = message.role === 'assistant';
  const [showSavePopover, setShowSavePopover] = useState(false);
  const timestamp = useMemo(() => {
    if (!message.createdAt) return '';
    const date = new Date(message.createdAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  }, [message.createdAt]);

  // Check if this message is a saved prompt
  const isSavedPrompt = useMemo(() => {
    if (!savedPrompts || !message.content) return false;
    return savedPrompts.some((p) => p.text.trim() === message.content.trim());
  }, [savedPrompts, message.content]);

  return (
    <div
      className={cn(
        'flex items-start gap-3',
        isAssistant ? 'justify-start' : 'justify-end',
      )}
    >
      {isAssistant && (
        <div className='mt-1 flex h-8 w-8 items-center justify-center rounded-full border bg-white shadow-sm dark:bg-slate-950'>
          <Bot className='h-4 w-4 text-primary' />
        </div>
      )}
      <div
        className={cn(
          'max-w-full rounded-2xl border px-4 py-2 text-sm shadow-sm md:max-w-[75%]',
          isAssistant
            ? 'bg-muted/60 text-foreground'
            : 'bg-primary text-primary-foreground',
        )}
      >
        <div className='leading-relaxed'>
          {isAssistant ? (
            <div className='prose prose-sm max-w-none dark:prose-invert'>
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <p className='mb-2 last:mb-0'>{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className='mb-2 ml-4 list-disc'>{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className='mb-2 ml-4 list-decimal'>{children}</ol>
                  ),
                  li: ({ children }) => <li className='mb-1'>{children}</li>,
                  strong: ({ children }) => (
                    <strong className='font-semibold'>{children}</strong>
                  ),
                  em: ({ children }) => <em className='italic'>{children}</em>,
                }}
              >
                {message.content || (message.status === 'streaming' ? '…' : '')}
              </ReactMarkdown>
            </div>
          ) : (
            <div className='whitespace-pre-wrap'>
              {message.content || (message.status === 'streaming' ? '…' : '')}
            </div>
          )}
        </div>
        {!isAssistant && message.content && (
          <>
            {isSavedPrompt ? (
              <div className='mt-1 flex items-center gap-1 text-xs text-primary-foreground/70'>
                <Check className='h-3 w-3' />
                <span>Used saved prompt</span>
              </div>
            ) : onSave && chatMode !== 'chat' ? (
              <Popover open={showSavePopover} onOpenChange={setShowSavePopover}>
                <PopoverTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='mt-1 h-6 px-2 text-xs text-primary-foreground hover:bg-primary-foreground/10'
                  >
                    <BookmarkPlus className='h-3 w-3 mr-1' />
                    Save as prompt
                  </Button>
                </PopoverTrigger>
                <PopoverContent align='start' side='bottom' className='w-auto'>
                  <SavePromptForm
                    defaultValue={message.content}
                    chatMode={chatMode}
                    onSave={async (text, mode) => {
                      await onSave(text, mode);
                      setShowSavePopover(false);
                    }}
                    onCancel={() => setShowSavePopover(false)}
                  />
                </PopoverContent>
              </Popover>
            ) : null}
          </>
        )}
        {timestamp ? (
          <div
            className={cn(
              'mt-1 text-[11px]',
              isAssistant ? 'text-muted-foreground' : 'text-blue-100',
            )}
          >
            {timestamp}
          </div>
        ) : null}
        {isAssistant && message.thought && (
          <div className='mt-2 text-xs italic text-muted-foreground'>
            {message.thought}
          </div>
        )}
        {isAssistant && message.sources?.length ? (
          <div className='mt-3'>
            <p className='text-xs font-semibold text-muted-foreground mb-2'>
              Sources
            </p>
            <div className='flex flex-wrap gap-1.5'>
              {message.sources.map((source, index) => (
                <button
                  key={index}
                  type='button'
                  onClick={() => onSourceClick?.(message, index)}
                  className='inline-flex items-center justify-center h-6 px-2 rounded-md border border-primary/30 bg-primary/10 text-xs font-medium text-primary hover:bg-primary/20 hover:border-primary/50 transition-colors'
                  title={source.title || `Source ${index + 1}`}
                >
                  [{index + 1}]
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {isAssistant && message.suggestChatMode && onSwitchToChatMode && (
          <div className='mt-3 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2'>
            <MessageCircle className='h-4 w-4 text-primary' />
            <p className='flex-1 text-xs text-muted-foreground'>
              Want to discuss these findings?
            </p>
            <Button
              variant='default'
              size='sm'
              className='h-7 text-xs'
              onClick={onSwitchToChatMode}
            >
              <MessageCircle className='mr-1 h-3 w-3' />
              Switch to Discuss
            </Button>
          </div>
        )}
        {message.status === 'error' && message.error && (
          <p className='mt-2 text-xs text-destructive'>{message.error}</p>
        )}
      </div>
      {!isAssistant && (
        <div className='mt-1 flex h-8 w-8 items-center justify-center rounded-full border bg-white shadow-sm dark:bg-slate-950'>
          <User className='h-4 w-4 text-muted-foreground' />
        </div>
      )}
    </div>
  );
};

export const CollectionChatCard = ({
  orgId,
  collectionId,
  collectionName,
  authToken,
  className,
  examplePrompts = DEFAULT_EXAMPLES,
  disabled = false,
  onCustomizePrompt,
  customPromptActive = false,
  onOpenChatFilters,
  isAdmin = false,
  savedPrompts,
  onSavePrompt,
  onDeletePrompt,
  onReorderPrompts,
}: CollectionChatCardProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isManagingPrompts, setIsManagingPrompts] = useState(false);
  const [isPromptsCollapsed, setIsPromptsCollapsed] = useState(true);
  const [sourcesState, setSourcesState] = useState<{
    sources: CollectionChatMessage['sources'];
    highlightedIndex: number;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledAssistantId = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const hasAutoFocusedRef = useRef(false);

  const {
    messages,
    sendMessage,
    resetConversation,
    stop,
    isStreaming,
    error,
    addLocalNotice,
  } = useCollectionChat({
    orgId,
    collectionId,
    token: authToken,
    includeThoughts: true,
  });

  const canSend = inputValue.trim().length > 0 && !isStreaming && !disabled;
  const isReady = Boolean(orgId && collectionId && !disabled);
  const chatModeStorageKey = useMemo(
    () =>
      collectionId
        ? `collection-chat-mode-${collectionId}`
        : 'collection-chat-mode',
    [collectionId],
  );
  const [chatMode, setChatMode] = useState<string>('search');
  const [scoreThreshold, setScoreThreshold] = useState<number>(() => 0.5);
  const scoreStorageKey = useMemo(
    () =>
      collectionId
        ? `collection-chat-threshold-${collectionId}`
        : 'collection-chat-threshold',
    [collectionId],
  );
  const [limit, setLimit] = useState<number>(() => 25);
  const limitStorageKey = useMemo(
    () =>
      collectionId
        ? `collection-chat-limit-${collectionId}`
        : 'collection-chat-limit',
    [collectionId],
  );

  // Determine if Discuss mode should be available (only after a search with results)
  const showDiscussMode = useMemo(() => {
    return messages.some(
      (msg) =>
        msg.role === 'assistant' && msg.sources && msg.sources.length > 0,
    );
  }, [messages]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(scoreStorageKey);
      if (stored) {
        const num = parseFloat(stored);
        if (!Number.isNaN(num)) {
          setScoreThreshold(num);
        }
      }
    } catch {
      setScoreThreshold(0.5);
    }
  }, [scoreStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = localStorage.getItem(limitStorageKey);
      if (stored) {
        const num = parseInt(stored, 10);
        if (!Number.isNaN(num) && num >= 1 && num <= 50) {
          setLimit(num);
        }
      }
    } catch {
      setLimit(25);
    }
  }, [limitStorageKey]);

  // Auto-focus textarea when chat is ready (only once on initial mount)
  useEffect(() => {
    if (isReady && textareaRef.current && !hasAutoFocusedRef.current) {
      textareaRef.current.focus();
      hasAutoFocusedRef.current = true;
    }
  }, [isReady]);

  // Auto-scroll to bottom during streaming so thought text stays visible
  useEffect(() => {
    if (isStreaming && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [isStreaming, messages]);

  // If user is in chat/discuss mode but shouldn't be (e.g., after reset), switch to search
  useEffect(() => {
    if (chatMode === 'chat' && !showDiscussMode) {
      setChatMode('search');
    }
  }, [chatMode, showDiscussMode]);

  const handleChatModeChange = (mode: string) => {
    setChatMode(mode);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(chatModeStorageKey, mode);
      } catch {
        // ignore storage errors
      }
    }
    const modeLabel =
      mode === 'collection-info'
        ? 'collection info'
        : mode === 'chat'
          ? 'discuss'
          : 'search';
    addLocalNotice(`Chat mode changed to ${modeLabel} mode.`);
  };

  const handleResetConversation = () => {
    resetConversation();
    setInputValue('');
  };

  const handleThresholdChange = (value: string) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return;
    setScoreThreshold(num);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(scoreStorageKey, num.toString());
      } catch {
        // ignore
      }
    }
    addLocalNotice(`Search score threshold set to ${num.toFixed(1)}.`);
  };

  const handleLimitChange = (value: string) => {
    const num = parseInt(value, 10);
    if (Number.isNaN(num) || num < 1 || num > 50) return;
    setLimit(num);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(limitStorageKey, num.toString());
      } catch {
        // ignore
      }
    }
    addLocalNotice(`Search result limit set to ${num}.`);
  };

  // Determine which prompts to display (priority: saved > example > default)
  const displayedPrompts = useMemo(() => {
    if (savedPrompts && savedPrompts.length > 0) {
      return savedPrompts;
    }
    if (examplePrompts && examplePrompts.length > 0) {
      return examplePrompts;
    }
    return DEFAULT_EXAMPLES;
  }, [savedPrompts, examplePrompts]);

  // Handler for saving a prompt (passed to ChatMessageBubble)
  const handleSavePrompt = useCallback(
    async (content: string, mode: string) => {
      if (!onSavePrompt) return;
      await onSavePrompt(content, mode);
    },
    [onSavePrompt],
  );

  // Handler for clicking a source citation
  const handleSourceClick = useCallback(
    (message: CollectionChatMessage, sourceIndex: number) => {
      if (message.sources && message.sources.length > 0) {
        setSourcesState({
          sources: message.sources,
          highlightedIndex: sourceIndex,
        });

        // Scroll to the highlighted source after sidebar opens
        setTimeout(() => {
          const element = document.getElementById(`source-${sourceIndex}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 350);
      }
    },
    [],
  );

  const description = useMemo(() => {
    if (!collectionName) {
      return 'Ask anything about this Qdrant collection.';
    }
    return `Ask anything about ${collectionName}. We will include recent chat history automatically.`;
  }, [collectionName]);

  const handleSubmit = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!canSend) return;
    const value = inputValue.trim();
    setInputValue('');
    // Auto-collapse prompts after first message
    if (!isPromptsCollapsed && messages.length === 0) {
      setIsPromptsCollapsed(true);
    }
    await sendMessage({
      message: value,
      query: value,
      chatMode,
      scoreThreshold: chatMode === 'search' ? scoreThreshold : undefined,
      limit: chatMode === 'search' ? limit : undefined,
    });
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) {
        void handleSubmit();
      }
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    const lastAssistant = [...messages]
      .reverse()
      .find((m) => m.role === 'assistant');
    const assistantId = lastAssistant?.id || null;
    if (assistantId && assistantId !== lastScrolledAssistantId.current) {
      const target = document.getElementById(assistantId);
      if (target) {
        const container = scrollRef.current;
        const containerTop = container.getBoundingClientRect().top;
        const targetTop = target.getBoundingClientRect().top;
        const offset = targetTop - containerTop + container.scrollTop - 16;
        container.scrollTo({ top: offset, behavior: 'smooth' });
        lastScrolledAssistantId.current = assistantId;
        return;
      }
    }
  }, [messages]);

  const handleExportTranscript = useCallback(() => {
    if (!messages.length) {
      return;
    }

    const header = [
      'Collection chat transcript',
      `Collection: ${collectionName || collectionId || 'unknown'}`,
      `Generated: ${new Date().toISOString()}`,
      '',
    ];

    const body = messages.flatMap((message, index) => {
      const speaker = message.role === 'assistant' ? 'Assistant' : 'User';
      const lines = [`${index + 1}. ${speaker}`];
      const ts = message.createdAt
        ? new Date(message.createdAt).toISOString()
        : null;
      if (ts) {
        lines.push(`Timestamp: ${ts}`);
      }
      const content = message.content?.trim();
      if (content) {
        lines.push(content);
      } else if (message.status === 'error' && message.error) {
        lines.push(`(error) ${message.error}`);
      } else if (message.status === 'streaming') {
        lines.push('…');
      }
      if (message.thought) {
        lines.push(`Thought: ${message.thought}`);
      }
      if (message.sources?.length) {
        lines.push('Sources:');
        message.sources.forEach((source, sourceIndex) => {
          const label =
            source.title || source.url || `Source ${sourceIndex + 1}`;
          lines.push(`- ${label}`);
          if (source.url) {
            lines.push(`  ${source.url}`);
          }
          const metadataParts: string[] = [];
          if (source.external_id) {
            metadataParts.push(`external_id=${source.external_id}`);
          }
          if (source.datetime) {
            metadataParts.push(`datetime=${source.datetime}`);
          }
          if (source.index_time) {
            metadataParts.push(`index_time=${source.index_time}`);
          }
          if (source.category) {
            metadataParts.push(`category=${source.category}`);
          }
          if (metadataParts.length) {
            lines.push(`  metadata: ${metadataParts.join(' | ')}`);
          }
        });
      }
      lines.push('');
      return lines;
    });

    const transcript = [...header, ...body].join('\n');
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const filename = `collection-chat-${collectionId || 'transcript'}-${Date.now()}.txt`;
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [collectionId, collectionName, messages]);

  return (
    <Card className={cn('shadow-sm', className)}>
      <CardHeader className='space-y-2'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <CardTitle className='flex items-center gap-2 text-base font-semibold'>
              <MessageCircle className='h-4 w-4 text-primary' />
              Collection chat
            </CardTitle>
            <Badge variant='outline' className='text-xs'>
              Beta
            </Badge>
            {customPromptActive && (
              <Badge variant='default' className='text-xs bg-primary/90'>
                Custom prompt
              </Badge>
            )}
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 px-2 text-xs text-foreground/80 hover:text-foreground'
              onClick={handleExportTranscript}
              disabled={!messages.length}
            >
              <Download className='mr-1.5 h-3.5 w-3.5' />
              Export chat
            </Button>
            {onCustomizePrompt && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2 text-xs text-foreground/80 hover:text-foreground'
                onClick={onCustomizePrompt}
              >
                <Wand2 className='mr-1.5 h-3.5 w-3.5' />
                Customize prompt
              </Button>
            )}
            {onOpenChatFilters && isAdmin && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2 text-xs text-foreground/80 hover:text-foreground'
                onClick={onOpenChatFilters}
              >
                <SlidersHorizontal className='mr-1.5 h-3.5 w-3.5' />
                Chat filters
              </Button>
            )}
          </div>
        </div>
        <p className='text-sm text-muted-foreground'>{description}</p>
      </CardHeader>
      <CardContent className='space-y-4'>
        {!isReady && (
          <div className='rounded-md border border-dashed bg-muted/50 p-3 text-sm text-muted-foreground'>
            Select an organization and collection to start chatting.
          </div>
        )}
        {error && (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        )}

        {/* Chat Mode Selector - Segmented Control */}
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <div className='inline-flex rounded-lg border bg-muted/50 p-1'>
              <button
                type='button'
                onClick={() => handleChatModeChange('search')}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                  chatMode === 'search'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Search
              </button>
              <button
                type='button'
                onClick={() => handleChatModeChange('collection-info')}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                  chatMode === 'collection-info'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Collection Info
              </button>
              {showDiscussMode && (
                <button
                  type='button'
                  onClick={() => handleChatModeChange('chat')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                    chatMode === 'chat'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  Discuss
                </button>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  className='inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground'
                  aria-label='Chat mode info'
                >
                  <Info className='h-4 w-4' />
                </button>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-xs text-xs'>
                <p>
                  <strong>Search mode:</strong> finds and summarizes documents
                  in this collection.
                </p>
                <p className='mt-1'>
                  <strong>Collection info:</strong> answers about collection
                  details like counts and metadata. Switch if you only need how
                  many points/documents or basic info.
                </p>
                {showDiscussMode && (
                  <p className='mt-1'>
                    <strong>Discuss mode:</strong> natural conversation to
                    discuss findings, ask follow-up questions, or analyze search
                    results without triggering new searches.
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Score Threshold & Limit - Only in Search Mode */}
          {chatMode === 'search' && (
            <div className='flex items-center gap-3'>
              {/* Score Threshold */}
              <div className='flex items-center gap-2'>
                <Select
                  value={scoreThreshold.toFixed(1)}
                  onValueChange={handleThresholdChange}
                >
                  <SelectTrigger className='h-8 w-36 text-xs'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      '0.2',
                      '0.3',
                      '0.4',
                      '0.5',
                      '0.6',
                      '0.7',
                      '0.8',
                      '0.9',
                      '1.0',
                    ].map((val) => (
                      <SelectItem key={val} value={val}>
                        Score ≥ {val}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className='inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground'
                      aria-label='Score threshold info'
                    >
                      <Info className='h-4 w-4' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side='top' className='max-w-xs text-xs'>
                    <p>
                      Minimum similarity score (0.0-1.0) for search results.
                      Higher values return only very relevant matches; lower
                      values include more results.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Result Limit */}
              <div className='flex items-center gap-2'>
                <Select
                  value={limit.toString()}
                  onValueChange={handleLimitChange}
                >
                  <SelectTrigger className='h-8 w-32 text-xs'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['5', '10', '15', '20', '25', '30', '40', '50'].map(
                      (val) => (
                        <SelectItem key={val} value={val}>
                          {val} results
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className='inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground'
                      aria-label='Result limit info'
                    >
                      <Info className='h-4 w-4' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side='top' className='max-w-xs text-xs'>
                    <p>
                      Maximum number of search results to return (1-50). More
                      results provide broader context but may include less
                      relevant matches.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </div>

        <div className='flex min-h-[360px] max-h-[60vh] md:max-h-[70vh] flex-col gap-4 rounded-2xl border bg-muted/40 p-4 overflow-hidden'>
          {/* Collapsible Quick Prompts Section */}
          {displayedPrompts.length > 0 && (
            <div className='border-b pb-3'>
              <button
                type='button'
                onClick={() => setIsPromptsCollapsed(!isPromptsCollapsed)}
                className='flex w-full items-center justify-between text-sm font-medium text-foreground hover:text-primary transition-colors'
              >
                <span className='flex items-center gap-2'>
                  <Sparkles className='h-4 w-4' />
                  Quick Prompts
                  <Badge variant='secondary' className='text-xs'>
                    {displayedPrompts.length}
                  </Badge>
                </span>
                {isPromptsCollapsed ? (
                  <ChevronDown className='h-4 w-4' />
                ) : (
                  <ChevronUp className='h-4 w-4' />
                )}
              </button>
              {!isPromptsCollapsed && (
                <div className='mt-3'>
                  <div className='flex flex-wrap items-start gap-2'>
                    {displayedPrompts.map((prompt, index) => {
                      // Handle both SavedPrompt objects and string prompts
                      const promptText =
                        typeof prompt === 'string' ? prompt : prompt.text;
                      const promptChatMode =
                        typeof prompt === 'string'
                          ? undefined
                          : prompt.chat_mode;

                      return (
                        <button
                          key={`${promptText}-${index}`}
                          type='button'
                          className='rounded-full border px-3 py-1 text-xs font-medium text-foreground transition hover:bg-background'
                          onClick={() => {
                            setInputValue(promptText);
                            // Auto-switch chat mode if this is a saved prompt with a chat_mode
                            if (promptChatMode) {
                              setChatMode(promptChatMode);
                            }
                          }}
                          disabled={!isReady}
                        >
                          {promptText}
                        </button>
                      );
                    })}
                  </div>
                  {savedPrompts && savedPrompts.length > 0 && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='mt-2 text-xs'
                      onClick={() => setIsManagingPrompts(true)}
                    >
                      <Edit2 className='h-3 w-3 mr-1' />
                      Manage prompts
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Messages Area */}
          <div
            ref={scrollRef}
            className='flex-1 space-y-4 overflow-y-auto pr-1 scroll-smooth'
          >
            {messages.length === 0 ? (
              <div className='flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground'>
                <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-md dark:bg-slate-950'>
                  <Sparkles className='h-5 w-5 text-primary' />
                </div>
                <p className='font-medium text-foreground'>
                  Start a chat about this collection
                </p>
                <p className='mt-1 max-w-sm'>
                  Ask questions about the sources, ingestion status, or indexed
                  points.
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <div key={message.id} id={message.id}>
                  <ChatMessageBubble
                    message={message}
                    onSave={handleSavePrompt}
                    savedPrompts={savedPrompts}
                    chatMode={chatMode}
                    onSwitchToChatMode={() => handleChatModeChange('chat')}
                    onSourceClick={handleSourceClick}
                  />
                </div>
              ))
            )}
          </div>
          <form className='space-y-3' onSubmit={handleSubmit}>
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isReady
                  ? 'Ask anything… (press Enter to send, Shift + Enter for a new line)'
                  : 'Chat is unavailable until a collection loads.'
              }
              disabled={!isReady}
              rows={4}
              className='bg-white'
            />
            <div className='flex flex-wrap items-center justify-between gap-2'>
              {isStreaming ? (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  Generating a response…
                </div>
              ) : (
                <div className='text-xs text-muted-foreground'>
                  {isReady
                    ? 'Press Enter to send. Shift + Enter adds a new line.'
                    : 'Chat is disabled for this collection.'}
                </div>
              )}
              <div className='flex items-center gap-2'>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={handleResetConversation}
                  disabled={messages.length === 0 || isStreaming}
                >
                  <RotateCcw className='mr-1 h-3.5 w-3.5' />
                  Reset
                </Button>
                {isStreaming ? (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={stop}
                  >
                    <StopCircle className='mr-1 h-3.5 w-3.5' />
                    Stop
                  </Button>
                ) : null}
                <Button type='submit' disabled={!canSend}>
                  {isStreaming ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <SendHorizontal className='mr-2 h-4 w-4' />
                  )}
                  Send
                </Button>
              </div>
            </div>
          </form>
        </div>
      </CardContent>
      <SourceDetailsSidebar
        sources={sourcesState?.sources || []}
        highlightedIndex={sourcesState?.highlightedIndex ?? null}
        isOpen={sourcesState !== null}
        onClose={() => setSourcesState(null)}
      />
      {savedPrompts && savedPrompts.length > 0 && (
        <ManagePromptsModal
          isOpen={isManagingPrompts}
          onOpenChange={setIsManagingPrompts}
          prompts={savedPrompts}
          onDelete={onDeletePrompt || (async () => {})}
          onReorder={onReorderPrompts || (async () => {})}
        />
      )}
    </Card>
  );
};
